import { useEffect, useState } from "react";
import { get } from "lodash-es";
import { useTheme, type Theme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import type { Craft } from "../../models/Craft";
import Grid2 from "@mui/material/Grid2";
import { ErrorModal } from "@dexteel/mesf-core";
import { getCrafts } from "@/pages/task-list/repositorie/TaskListRepositorie";
import { getCraftLetter } from "@/utils/craftLetter";

const getStyles = (craftId: number, crafts: Craft[], theme: Theme) => {
	return {
		fontWeight: !!crafts.find((p) => p.CraftId === craftId)
			? theme.typography.fontWeightRegular
			: theme.typography.fontWeightMedium,
	};
};

type Props = {
	value: number;
	onChange: Function;
	disabled?: boolean;
};

export const CraftPicker = ({ value, onChange, disabled }: Props) => {
	const theme = useTheme();
	const [crafts, setCrafts] = useState<Craft[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		let canceled = false;
		setIsLoading(true);
		getCrafts()
			.then((res) => {
				if (res.ok) {
					const craftsData = get(res, "data.tables[0].rows", null);
					if (craftsData) {
						if (canceled) return;
						setCrafts(craftsData);
					} else console.log("algo falló");
					setIsLoading(false);
				} else {
					setError(res.message);
					setIsLoading(false);
				}
			})
			.finally(() => {
				if (canceled) return;
			});
		return () => {
			canceled = true;
		};
	}, []);

	return (
		<Box sx={{ "& .MuiSelect-root": { backgroundColor: "#FFF" } }}>
			{crafts.length ? (
				<FormControl
					variant="outlined"
					fullWidth
					sx={{
						marginTop: "8px",
						"& .MuiOutlinedInput-root": {
							height: "37px",
							...(!disabled && {
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: "#3F51B5",
									borderWidth: "2px",
								},
							}),
						},
					}}
				>
					<InputLabel
						id="crafts-picker-label"
						shrink={!!value}
						sx={{
							top: "-4px",
							"&.MuiInputLabel-shrink": {
								top: 0,
							},
						}}
					>
						Crafts
					</InputLabel>
					<Select
						disabled={disabled ? disabled : false}
						labelId="crafts-picker-label"
						id="crafts-picker"
						value={value}
						onChange={onChange as any}
						label="Crafts"
						renderValue={(selected) => {
							const selectedCraft = crafts.find((c) => c.CraftId === selected);
							if (!selectedCraft) return "";
							return (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Box
										sx={{
											height: 24,
											width: 24,
											minWidth: 24,
											borderRadius: 0.5,
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											background: selectedCraft.Color || "#FFFFFF",
											color: "#FFF !important",
											WebkitTextFillColor: "#FFF !important",
											fontWeight: 700,
											fontSize: "0.875rem",
											flexShrink: 0,
										}}
									>
										{getCraftLetter(
											selectedCraft.CraftName,
											selectedCraft.Code,
										)}
									</Box>
									<Box sx={{ fontSize: "0.9375rem" }}>
										{selectedCraft.CraftName}
									</Box>
								</Box>
							);
						}}
					>
						{crafts.map((craft) => (
							<MenuItem
								key={`craft-${craft.CraftId}`}
								value={craft.CraftId}
								sx={{
									...getStyles(craft.CraftId, crafts, theme),
									display: "flex",
									alignItems: "center",
									gap: 1.25,
									whiteSpace: "nowrap",
								}}
							>
								<Box
									sx={{
										height: 20,
										width: 20,
										minWidth: 20,
										borderRadius: 0.5,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										background: craft.Color || "#FFFFFF",
										color: "#FFF",
										fontWeight: 600,
										flexShrink: 0,
									}}
								>
									{getCraftLetter(craft.CraftName, craft.Code)}
								</Box>
								<Box sx={{ overflow: "visible" }}>{craft.CraftName}</Box>
							</MenuItem>
						))}
					</Select>
				</FormControl>
			) : (
				<></>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
